import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Fetches the tenant's enabled plan-modules once per session.
 *
 * Returns:
 *   modules: Set<string>   — enabled module slugs
 *   loaded:  boolean       — fetched at least once
 *   isSuper: boolean       — current user is super admin (bypasses gating)
 *   has:     (slug) => bool — convenience checker
 *
 * Designed to be idempotent: if the backend errors or user isn't authenticated
 * we treat all modules as enabled (graceful degradation — never hide UI due to
 * a network blip).
 */
const useCompanyModules = () => {
  const [modules, setModules] = useState(null);   // null = loading, Set otherwise
  const [isSuper, setIsSuper] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) { setModules(new Set()); return; }
    axios.get(`${API}/company/plan-modules`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (cancelled) return;
        setModules(new Set(r.data.modules || []));
        setIsSuper(!!r.data.is_super_admin);
      })
      .catch(() => {
        // On error, treat all as enabled to avoid hiding anything due to a glitch
        if (!cancelled) setModules(null);
      });
    return () => { cancelled = true; };
  }, []);

  // Treat null (error / still loading) as "show everything" so the sidebar
  // paints immediately on page load and stays visible if the modules
  // endpoint ever flakes. This is the correct UX default — the backend
  // still enforces access per route.
  const has = (slug) => {
    if (!slug) return true;
    if (isSuper) return true;
    if (modules === null) return true;
    return modules.has(slug);
  };

  return { modules, isSuper, has, loaded: modules !== null || isSuper };
};

export default useCompanyModules;
